// Friend graph edge calls (ticket 302). Reuses edgeApi.ts's `call()` choke
// point verbatim -- same shape as lib/edgeApi.ts's own exports, one function
// per route, zod-parsed request/response.
import {
  BlockList,
  FriendList,
  FriendshipActionResponse,
  FriendshipView,
  OkResponse,
  SendFriendRequestRequest,
  UserSearchResponse,
} from "@proef/contract";
import type { Page } from "../api";
import { call } from "../edgeApi";

function post(body: unknown): RequestInit {
  return { method: "POST", body: JSON.stringify(body) };
}

/**
 * GET /v1/friends has no limit/offset/cursor support server-side -- it
 * always returns the caller's full non-blocked friendship list in one shot
 * (apps/edge/src/friendships.ts), `nextCursor` permanently null. `limit`/
 * `offset` are accepted here only so existing call sites built around
 * usePaged.ts's `(offset) => Promise<Page<T>>` signature keep compiling;
 * neither is sent on the wire. `total` is `items.length`, not a server
 * count, so usePaged's "load more" affordance (`items.length < total`)
 * never lights up past the first page -- there is no second page to fetch.
 */
export async function listFriends(_limit = 50, _offset = 0): Promise<Page<FriendshipView>> {
  const response = await call(FriendList, "/v1/friends");
  return { items: response.items, total: response.items.length };
}

/** A request answers with its row, or `{ok:true}` when the edge drops it silently (the target
 * blocked the sender), which must look like any other sent request. */
const SentFriendRequest = FriendshipView.or(OkResponse);

/** `clientRequestId` defaults to a fresh uuid; callers queueing an offline
 * send pass their own stored id so a retry after reconnect is idempotent. */
export function sendFriendRequest(
  targetUserId: string,
  clientRequestId: string = crypto.randomUUID(),
): Promise<FriendshipView | OkResponse> {
  return call(
    SentFriendRequest,
    "/v1/friends/requests",
    post(SendFriendRequestRequest.parse({ clientRequestId, targetUserId })),
  );
}

/** Accept answers with the updated row; decline, cancel, unfriend and block remove it and answer
 * `{ok:true}`, so both shapes parse. */
function transition(action: "accept" | "decline" | "cancel" | "unfriend" | "block") {
  return (friendshipId: string): Promise<FriendshipActionResponse> =>
    call(
      FriendshipActionResponse,
      `/v1/friends/requests/${encodeURIComponent(friendshipId)}/${action}`,
      post({}),
    );
}

export const acceptFriendRequest = transition("accept");
export const declineFriendRequest = transition("decline");
export const cancelFriendRequest = transition("cancel");
export const unfriend = transition("unfriend");
export const blockFriend = transition("block");

/** Blocks anyone, friend or not; the other person is never told. */
export function blockUser(userId: string): Promise<OkResponse> {
  return call(OkResponse, `/v1/users/${encodeURIComponent(userId)}/block`, post({}));
}

export function unblockUser(userId: string): Promise<OkResponse> {
  return call(OkResponse, `/v1/users/${encodeURIComponent(userId)}/block`, { method: "DELETE" });
}

export function listBlockedUsers(): Promise<BlockList> {
  return call(BlockList, "/v1/blocks");
}

/**
 * AddFriendSheet's search box -- discoverable people-search (human overrides #2/#3):
 * GET /v1/friends/search, substring/prefix match on handle/display name, cursor-paginated.
 * The caller enforces MIN_SEARCH_QUERY_LENGTH before calling (the Worker 400s below it anyway,
 * but the sheet never fires that request in the first place -- design.md's "never an implicit
 * browse-all" rule applies to the client's own behaviour too, not just the response it'd get).
 */
export function searchFriendCandidates(
  query: string,
  cursor?: string,
): Promise<UserSearchResponse> {
  const params = new URLSearchParams({ q: query });
  if (cursor !== undefined) params.set("cursor", cursor);
  return call(UserSearchResponse, `/v1/friends/search?${params.toString()}`);
}

export type { FriendshipDirection, FriendshipStatus, FriendshipView } from "./friendship";
